/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ClientInfoForm, ServiceDetailsForm, AppointmentDetailsForm, FeesForm } from 'components/modules';
import { StepperForm, ControlButtons } from 'components/elements';
import { routes } from 'const';
import { useBookingActions, useStateCallback, useStripeContext, useFetchConfiguration, useGoogleRecaptcha } from 'hooks';
import api from 'client';

function Booking() {
  const { t } = useTranslation('labels');
  const history = useHistory();
  const { i18n } = useTranslation();
  const { isEnabledField, isEnabledSection, isLoading } = useFetchConfiguration();
  const isEnabledPayments = isEnabledField('payments');
  const { isEnabled: paymentsEnabled, setSettings: setStripeSettings } = useStripeContext();

  useEffect(() => {
    if (isEnabledPayments && !paymentsEnabled) {
      api.post('/apis/v2/online_intake/setup_payment', {}).then((data) => {
        setStripeSettings((s) => ({
          ...s,
          clientSecret: data.data.stripeClientSecret,
          intentId: data.data.setupIntentId,
          accountId: data.data.stripeAccount,
          isEnabled: isEnabledPayments,
        }));
      });
    } else {
      setStripeSettings((s) => ({ ...s, isEnabled: isEnabledPayments }));
    }
  }, [isEnabledPayments]);

  const { bookOnlineIntakeMutation } = useBookingActions();
  const formId = 'BookingForm';

  const [activeStep, setActiveStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  const [clientInfoFormData, setClientInfoFormData] = useStateCallback({});
  const [serviceDetailsFormData, setServiceDetailsFormData] = useStateCallback({});
  const [appointmentDetailsFormData, setAppointmentDetailsFormData] = useStateCallback({});
  const [feesFormData, setFeesFormData] = useStateCallback({});
  const { getToken: getRecaptchaToken } = useGoogleRecaptcha();

  const submitWizzard = async (lastStepData) => {
    if (!processing) {
      await setProcessing(true);
      const token = await getRecaptchaToken();
      const config = {
        headers: {
          'Recaptcha-Token': token,
        },
      };
      await bookOnlineIntakeMutation.mutateAsync({
        ...clientInfoFormData,
        ...serviceDetailsFormData,
        ...appointmentDetailsFormData,
        ...feesFormData,
        ...lastStepData,
        config,
      });
      history.push(routes.unauthenticatedBookingThankYou);
    }
  };

  const stepsCount = 1 + isEnabledSection('service_details') + isEnabledSection('appointment_details') + isEnabledSection('fees');
  const isLastStep = activeStep === stepsCount - 1;

  const getSubmitFormFunction = (setFormData) => (data) => {
    setFormData(data, (formData) => {
      if (isLastStep) {
        submitWizzard(formData);
      } else {
        handleNext();
      }
    });
  };

  const steps = useMemo(() => {
    const stp = [
      {
        label: t('client_information'),
        component: ClientInfoForm,
        componentProps: {
          formId,
          onSubmit: getSubmitFormFunction(setClientInfoFormData),
          ...clientInfoFormData,
        },
      },
    ];

    if (isEnabledSection('service_details')) {
      stp.push({
        label: t('service_details'),
        component: ServiceDetailsForm,
        componentProps: {
          formId,
          onSubmit: getSubmitFormFunction(setServiceDetailsFormData),
          ...serviceDetailsFormData,
        },
      });
    }
    if (isEnabledSection('appointment_details')) {
      stp.push({
        label: t('appointment_details'),
        component: AppointmentDetailsForm,
        componentProps: {
          formId,
          onSubmit: getSubmitFormFunction(setAppointmentDetailsFormData),
          ...appointmentDetailsFormData,
        },
      });
    }
    if (isEnabledSection('fees')) {
      stp.push({
        label: t('fees'),
        component: FeesForm,
        componentProps: {
          formId,
          onSubmit: getSubmitFormFunction(setFeesFormData),
          ...feesFormData,
        },
      });
    }
    return stp;
  }, [i18n.language, isLoading, handleNext]);
  const step = steps[activeStep];

  return (
    <StepperForm
      activeStep={activeStep}
      steps={steps.map(({ label }) => label)}
      navigation={
        <ControlButtons
          formId={formId}
          onClickNext={handleNext}
          onClickBack={handleBack}
          isLastStep={isLastStep}
          isFirstStep={activeStep === 0}
          canClickBack={!bookOnlineIntakeMutation.isLoading}
          canClickNext={!bookOnlineIntakeMutation.isLoading}
          processing={processing}
        />
      }
    >
      <step.component {...step.componentProps} />
    </StepperForm>
  );
}

export default Booking;
